/**
 * Consola superadmin responsive: tenants, planes, avisos, auditoría, notas Stripe
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Shield, Plus, ArrowLeft, Megaphone, CreditCard, ScrollText } from 'lucide-react'
import { apiClient } from '@/services/apiClient'

type Tab = 'tenants' | 'announcements' | 'audit' | 'billing'

type TenantRow = {
  id: string
  name: string
  slug: string
  active: boolean
  plan: string
  subscriptionStatus: string
  trialEndsAt: string | null
  _count: { users: number; nodes: number; cables: number }
}

type Announcement = {
  id: string
  title: string
  body: string
  active: boolean
  startsAt: string | null
  endsAt: string | null
  createdAt: string
}

export function PlatformAdminPage() {
  const [tab, setTab] = useState<Tab>('tenants')
  const [tenants, setTenants] = useState<TenantRow[]>([])
  const [audit, setAudit] = useState<
    Array<{ id: string; action: string; createdAt: string; targetTenantId: string | null }>
  >([])
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [newName, setNewName] = useState('')
  const [newSlug, setNewSlug] = useState('')
  const [annTitle, setAnnTitle] = useState('')
  const [annBody, setAnnBody] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [tenantSearch, setTenantSearch] = useState('')

  const filteredTenants = useMemo(() => {
    const q = tenantSearch.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter(
      (t) =>
        t.slug.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q)
    )
  }, [tenants, tenantSearch])

  const loadTenants = useCallback(async () => {
    setError(null)
    try {
      const t = await apiClient.get<TenantRow[]>('/api/platform/tenants')
      setTenants(t)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }, [])

  const loadAudit = useCallback(async () => {
    try {
      const a = await apiClient.get<typeof audit>('/api/platform/audit?limit=80')
      setAudit(a)
    } catch {
      /* ignore */
    }
  }, [])

  const loadAnnouncements = useCallback(async () => {
    try {
      const list = await apiClient.get<Announcement[]>('/api/platform/announcements')
      setAnnouncements(list)
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    if (tab === 'tenants') void loadTenants()
    if (tab === 'audit') void loadAudit()
    if (tab === 'announcements') void loadAnnouncements()
  }, [tab, loadTenants, loadAudit, loadAnnouncements])

  const createTenant = async () => {
    setError(null)
    try {
      await apiClient.post('/api/platform/tenants', { name: newName, slug: newSlug })
      setNewName('')
      setNewSlug('')
      loadTenants()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  const toggleActive = async (id: string, active: boolean) => {
    try {
      await apiClient.patch(`/api/platform/tenants/${id}`, { active })
      loadTenants()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  const patchPlan = async (id: string, plan: string, subscriptionStatus?: string) => {
    try {
      await apiClient.patch(`/api/platform/tenants/${id}/plan`, {
        plan,
        ...(subscriptionStatus ? { subscriptionStatus } : {}),
      })
      loadTenants()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  const createAnnouncement = async () => {
    setError(null)
    try {
      await apiClient.post('/api/platform/announcements', { title: annTitle, body: annBody })
      setAnnTitle('')
      setAnnBody('')
      loadAnnouncements()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  const toggleAnnouncement = async (id: string, active: boolean) => {
    try {
      await apiClient.patch(`/api/platform/announcements/${id}`, { active })
      loadAnnouncements()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  const deleteAnnouncement = async (id: string) => {
    if (!confirm('¿Eliminar aviso?')) return
    try {
      await apiClient.delete(`/api/platform/announcements/${id}`)
      loadAnnouncements()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 text-gray-900 dark:text-gray-100">
      <header className="sticky top-0 z-10 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-600 px-4 py-3 flex flex-wrap items-center gap-3">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al mapa
        </Link>
        <h1 className="text-lg font-semibold flex items-center gap-2 ml-auto sm:ml-0">
          <Shield className="w-5 h-5" />
          Plataforma
        </h1>
      </header>

      <nav className="flex flex-wrap gap-1 px-4 py-2 border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800">
        {(
          [
            ['tenants', 'Organizaciones', Plus],
            ['announcements', 'Avisos', Megaphone],
            ['audit', 'Auditoría', ScrollText],
            ['billing', 'Stripe', CreditCard],
          ] as const
        ).map(([id, label, Icon]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg ${
              tab === id
                ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800'
                : 'hover:bg-gray-100 dark:hover:bg-dark-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </nav>

      <main className="p-4 max-w-5xl mx-auto space-y-6">
        {error && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}

        {tab === 'tenants' && (
          <>
            <section className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-600 p-4">
              <h2 className="font-semibold mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Nueva organización
              </h2>
              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <input
                  placeholder="Nombre"
                  className="border rounded px-3 py-2 dark:bg-dark-700 flex-1 min-w-[140px]"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <input
                  placeholder="slug"
                  className="border rounded px-3 py-2 dark:bg-dark-700 flex-1 min-w-[140px]"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                />
                <button
                  type="button"
                  onClick={createTenant}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white"
                >
                  Crear
                </button>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="font-semibold">Organizaciones</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed max-w-3xl">
                Vista global para superadmin: aquí aparecen <strong>todas</strong> las organizaciones del sistema. En el mapa
                principal solo ves <strong>una</strong> a la vez (escribe su slug y pulsa Aplicar).
              </p>
              <input
                type="search"
                placeholder="Filtrar por slug, nombre o id…"
                value={tenantSearch}
                onChange={(e) => setTenantSearch(e.target.value)}
                className="w-full max-w-md border rounded-lg px-3 py-2 text-sm dark:bg-dark-700 dark:border-dark-600"
                aria-label="Filtrar organizaciones"
              />
              <div className="hidden md:block overflow-x-auto bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-600">
                <table className="w-full text-xs text-left">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-dark-600">
                      <th className="p-2">Slug</th>
                      <th className="p-2">Plan</th>
                      <th className="p-2">Estado</th>
                      <th className="p-2">U/N/C</th>
                      <th className="p-2">Activo</th>
                      <th className="p-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTenants.map((t) => (
                      <tr key={t.id} className="border-b border-gray-100 dark:border-dark-700">
                        <td className="p-2 font-mono">{t.slug}</td>
                        <td className="p-2">
                          <select
                            className="border rounded dark:bg-dark-700 text-xs"
                            value={t.plan}
                            onChange={(e) => patchPlan(t.id, e.target.value)}
                          >
                            {['FREE', 'PRO', 'ENTERPRISE'].map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-2">
                          <select
                            className="border rounded dark:bg-dark-700 text-xs max-w-[120px]"
                            value={t.subscriptionStatus}
                            onChange={(e) => patchPlan(t.id, t.plan, e.target.value)}
                          >
                            {['TRIAL', 'ACTIVE', 'PAST_DUE', 'CANCELED', 'FREE_BASIC'].map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-2">
                          {t._count.users}/{t._count.nodes}/{t._count.cables}
                        </td>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={t.active}
                            onChange={(e) => toggleActive(t.id, e.target.checked)}
                          />
                        </td>
                        <td className="p-2 text-gray-500">
                          {t.trialEndsAt ? new Date(t.trialEndsAt).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="md:hidden space-y-3">
                {filteredTenants.map((t) => (
                  <li
                    key={t.id}
                    className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-600 p-3 text-sm space-y-2"
                  >
                    <div className="font-mono font-medium">{t.slug}</div>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={t.active}
                        onChange={(e) => toggleActive(t.id, e.target.checked)}
                      />
                      Activa
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <select
                        className="border rounded dark:bg-dark-700 text-xs flex-1 min-w-[100px]"
                        value={t.plan}
                        onChange={(e) => patchPlan(t.id, e.target.value)}
                      >
                        {['FREE', 'PRO', 'ENTERPRISE'].map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                      <select
                        className="border rounded dark:bg-dark-700 text-xs flex-1 min-w-[100px]"
                        value={t.subscriptionStatus}
                        onChange={(e) => patchPlan(t.id, t.plan, e.target.value)}
                      >
                        {['TRIAL', 'ACTIVE', 'PAST_DUE', 'CANCELED', 'FREE_BASIC'].map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="text-xs text-gray-500">
                      Usuarios {t._count.users} · Nodos {t._count.nodes} · Cables {t._count.cables}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}

        {tab === 'announcements' && (
          <section className="space-y-4">
            <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-600 p-4 space-y-2">
              <h2 className="font-semibold">Nuevo aviso global</h2>
              <input
                className="w-full border rounded px-3 py-2 dark:bg-dark-700"
                placeholder="Título"
                value={annTitle}
                onChange={(e) => setAnnTitle(e.target.value)}
              />
              <textarea
                className="w-full border rounded px-3 py-2 dark:bg-dark-700 min-h-[100px]"
                placeholder="Cuerpo (texto o HTML simple)"
                value={annBody}
                onChange={(e) => setAnnBody(e.target.value)}
              />
              <button
                type="button"
                onClick={createAnnouncement}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white"
              >
                Publicar
              </button>
            </div>
            <ul className="space-y-2">
              {announcements.map((a) => (
                <li
                  key={a.id}
                  className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-3 text-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <strong>{a.title}</strong>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={a.active}
                          onChange={(e) => toggleAnnouncement(a.id, e.target.checked)}
                        />
                        Activo
                      </label>
                      <button
                        type="button"
                        className="text-red-600 text-xs"
                        onClick={() => deleteAnnouncement(a.id)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mt-2 whitespace-pre-wrap">{a.body}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {tab === 'audit' && (
          <ul className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-600 p-4 text-xs font-mono space-y-1 max-h-[70vh] overflow-y-auto">
            {audit.map((log) => (
              <li key={log.id}>
                {new Date(log.createdAt).toISOString()} — {log.action}{' '}
                {log.targetTenantId ? `(tenant ${log.targetTenantId.slice(0, 8)}…)` : ''}
              </li>
            ))}
          </ul>
        )}

        {tab === 'billing' && (
          <div className="bg-white dark:bg-dark-800 rounded-xl border border-gray-200 dark:border-dark-600 p-4 prose dark:prose-invert max-w-none text-sm">
            <h2 className="font-semibold text-base mb-2">Stripe</h2>
            <p>
              Configura en el servidor: <code>STRIPE_SECRET_KEY</code>, <code>STRIPE_WEBHOOK_SECRET</code>,{' '}
              <code>STRIPE_PRICE_PRO_ID</code>, <code>APP_PUBLIC_URL</code>.
            </p>
            <p>
              Webhook URL: <code>/api/billing/stripe-webhook</code> (cuerpo raw JSON). Los tenants usan checkout desde
              Organización → Uso / plan.
            </p>
            <p>
              Webhook de prueba (JSON + header secreto): <code>POST /api/billing/webhook</code> con{' '}
              <code>BILLING_WEBHOOK_SECRET</code>.
            </p>
          </div>
        )}
      </main>
    </div>
  )
}
